import orderBy from "lodash.orderby";

import type {
  ExperienceActionDto,
  ExperienceStatus,
} from "@intelligems/ig-types";

export const actionTsDescSort = (
  a: ExperienceActionDto,
  b: ExperienceActionDto
): number => {
  if (a.actionTs < b.actionTs) {
    return 1;
  }
  if (a.actionTs > b.actionTs) {
    return -1;
  }
  return 0;
};

export const actionTsAscSort = (
  a: ExperienceActionDto,
  b: ExperienceActionDto
): number => {
  if (a.actionTs > b.actionTs) {
    return 1;
  }
  if (a.actionTs < b.actionTs) {
    return -1;
  }
  return 0;
};

export const getStartedAtTs = (experienceActions: ExperienceActionDto[]) => {
  if (!experienceActions?.length) {
    return null;
  }

  const sortedActions = [...experienceActions].sort(actionTsDescSort);
  const foundIndex = sortedActions.findIndex(
    (action) => action.action === "start"
  );
  if (foundIndex > -1) {
    return sortedActions[foundIndex];
  } else return null;
};

export const getPausedAtTs = (experienceActions: ExperienceActionDto[]) => {
  if (!experienceActions?.length) {
    return null;
  }

  const sortedActions = [...experienceActions].sort(actionTsDescSort);

  if (sortedActions[0].action === "pause") {
    return sortedActions[0];
  }
  return null;
};

export const getEndedAtTs = (experienceActions: ExperienceActionDto[]) => {
  if (!experienceActions?.length) {
    return null;
  }

  const sortedActions = [...experienceActions].sort(actionTsDescSort);

  if (sortedActions[0].action === "end") {
    return sortedActions[0];
  }
  return null;
};

export const getFirstStartedAtTs = (
  experienceActions: ExperienceActionDto[]
) => {
  const sortedActions = orderBy(experienceActions, ["actionTs"], ["asc"]);
  const foundIndex = sortedActions.findIndex(
    (action) => action.action === "start"
  );
  if (foundIndex > -1) {
    return sortedActions[foundIndex].actionTs;
  } else return null;
};

export const getLastEndedAtTs = (experienceActions: ExperienceActionDto[]) => {
  const sortedActions = orderBy(experienceActions, ["actionTs"], ["asc"]);
  const foundIndex = sortedActions.findIndex(
    (action) => action.action === "end"
  );
  if (foundIndex > -1) {
    return sortedActions[foundIndex].actionTs;
  } else return null;
};

export const getExperienceActionSpans = (
  experienceActions: ExperienceActionDto[],
  placeholderEndTs: number
) => {
  // Sort events by timestamp
  const sortedEvents = experienceActions.sort(
    (a, b) => a.actionTs - b.actionTs
  );

  const intervals: { start_ts: number; end_ts: number }[] = [];
  let currentStart: number | null = null;

  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];

    if (event.action === "start") {
      currentStart = event.actionTs;

      // Look ahead for the next pause/end event
      const nextEvent = sortedEvents
        .slice(i + 1)
        .find((e) => e.action === "pause" || e.action === "end");

      // If we found a pause/end event, use its timestamp as the end
      if (nextEvent) {
        intervals.push({
          start_ts: Math.floor(currentStart / 1000),
          end_ts: Math.floor(nextEvent.actionTs / 1000),
        });
      } else {
        // If no pause/end event found, use current timestamp
        intervals.push({
          start_ts: Math.floor(currentStart / 1000),
          end_ts: placeholderEndTs,
        });
      }
    }
  }

  return intervals;
};

const sortOrder = {
  started: 0,
  paused: 1,
  pending: 2,
  ended: 3,
};

export const sortByStatus = <
  T extends {
    status: ExperienceStatus;
    createdAtTs: number | null;
    startedAtTs: number | null;
  },
>(
  experiences: T[]
): T[] => {
  return [...experiences].sort((a, b) => {
    if (a.status === b.status) {
      if (a.status === "pending") {
        return (
          (b.createdAtTs || Number.MAX_VALUE) -
          (a.createdAtTs || Number.MAX_VALUE)
        );
      } else {
        return (
          (b.startedAtTs || Number.MAX_VALUE) -
          (a.startedAtTs || Number.MAX_VALUE)
        );
      }
    }
    return sortOrder[a.status || "ended"] - sortOrder[b.status || "ended"];
  });
};
